import type { NextPage } from "next";
import { useRouter } from "next/router";
import AllLayout from "./all-layout";
import ProductDetail from "./product-detail";
import ProductSkeleton from "./product-detail/ProductSkeleton";
import ChatModal from "./chat-modal";
import { useUser, useProduct, useRouterContext } from "../contexts";
import ListingGone from "./listing-gone";
import { useEffect, useRef, useState } from "react";
import { fetchProductById, bounceUnlessLeaving, watchPageLeaving, LISTING_GONE } from "./product-page-helpers";
import { getAnalytics } from "../utils/analytics";
import { useAdInsertHold } from "./ad-insert-batch";
import type { Product } from "../contexts/ProductProvider";
import type { ListingFetchFailure } from "../data/api/search";

interface ProductPageProps {
  productDetail?: Product;
  /** Set by the partner-link route when the listing API said this listing is gone. */
  goneListingId?: string;
}

const ProductPage: NextPage<ProductPageProps> = (props) => {
  const { query } = useRouterContext();
  // `isFallback` is read straight from the Next router (not the SSG-safe
  // context, which doesn't expose it) to gate the client fetch below.
  const { isFallback } = useRouter();
  const { setProduct, productDetail } = useProduct();
  const { profile } = useUser();
  const id = query.id;
  const product = productDetail;
  // Keyed by id, so flipping on to another listing clears it without a reset.
  const [goneId, setGoneId] = useState<string | null>(null);
  // The server already knows on a partner link to a listing that left; showing
  // the panel from the first paint means no client fetch to find out.
  const goneOnServer = id !== undefined && props.goneListingId === String(id);
  const gone = goneOnServer || (id !== undefined && goneId === String(id));

  // Ref tracks current productDetail without triggering the useEffect below.
  // This prevents a feedback loop when flipProduct() sets the product before
  // the router URL has updated — without the ref, the effect would see the
  // new productDetail mismatching the old URL id and reset the product.
  const productDetailRef = useRef(productDetail);
  // Keep the ref pointing at the latest productDetail on every render (read by the
  // sync effect below). Declared before that effect so it runs first.
  useEffect(() => {
    productDetailRef.current = productDetail;
  });

  // Sync product state when the URL or SSG props change.
  // - Full navigation (showcase clicks, direct nav): SSG props arrive with
  //   complete server data — always prefer them when they match the URL.
  // - Shallow navigation (flip buttons): SSG props stay stale — skip them
  //   and keep the product already set by flipProduct via the ref guard.
  // The cleanup function cancels stale fetches so a slow response for a
  // previous id doesn't overwrite the current product.
  useEffect(() => {
    let cancelled = false;

    if (!id) {
      return;
    }

    // SSG props match the URL — use them (full server-rendered data)
    if (props.productDetail && String(props.productDetail.id) === String(id)) {
      setProduct(props.productDetail);
      return;
    }

    // Already have the right product from context (e.g. flip buttons with shallow routing)
    if (productDetailRef.current && String(productDetailRef.current.id) === String(id)) {
      return;
    }

    // Gone per the server: record it as the client fetch would have, and do
    // not fetch. A crawler's fetch here is refused by the firewall (HTTP 403).
    if (goneOnServer) {
      getAnalytics()?.track({
        event_type: 'listing_unavailable',
        listing_id: Number(id),
        properties: { partner: typeof query.p === 'string' ? query.p : '' },
      });
      return;
    }

    // ISR fallback render: getStaticProps is already fetching this product's
    // data server-side and will arrive as `props.productDetail` on a following
    // render (handled by the branch above). Skip the client fetch so a cold
    // navigation doesn't hit /api/listings/[id] twice — the skeleton shows
    // until the server data lands.
    if (isFallback) {
      return;
    }

    // No matching data — fetch from API
    const page = watchPageLeaving();
    const bounce = (reason: string, detail: string | null, attempts: number | null) =>
      bounceUnlessLeaving(page, String(id), {
        reason,
        detail,
        attempts,
        ssg_props_id: props.productDetail ? String(props.productDetail.id) : null,
      });
    // Prod 2026-10-08: two bounces on a listing other readers were viewing in
    // the same minute, both logged as "not-found". A 404 now shows the gone
    // panel, so a bounce is a fetch that failed — and most of those paths log
    // nothing. Carry the reason into the bounce row.
    let failure: ListingFetchFailure | null = null;
    let attempts: number | null = null;
    void fetchProductById(id as string, (f, tries) => { failure = f; attempts = tries; })
      .then((fetched) => {
        if (cancelled) return;
        if (fetched === LISTING_GONE) {
          // Prod 2026-10-07: every bounce in a week was a partner widget
          // (weatherbug, realgm, kezi) still linking a listing that had been
          // off the site for days. Sending that reader to "/" lost every one of
          // them, and logged a warning for a 404 the fetch layer deliberately
          // does not count as an error. Say it is gone and keep them here.
          setGoneId(String(id));
          getAnalytics()?.track({
            event_type: 'listing_unavailable',
            listing_id: Number(id),
            properties: { partner: typeof query.p === 'string' ? query.p : '' },
          });
        } else if (fetched) {
          setProduct(fetched);
        } else {
          const why: ListingFetchFailure | null = failure;
          bounce(why?.reason ?? 'empty', why?.detail ?? null, attempts);
        }
      });
    // No .catch: fetchProductById catches and logs its own failures and
    // resolves null, which is the bounce above.

    return () => {
      cancelled = true;
      page.stop();
    };
    // query.p is read once for the analytics row; it must not re-run the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, props.productDetail, setProduct, isFallback, goneOnServer]);

  // The layout's banner ad mounts with the skeleton, ~450ms before the product
  // body brings its own three frames. Left alone that is two insertion batches,
  // so two widget auctions, and each one re-requests EVERY slot on the page —
  // both before anything has rendered, where the refresh guard cannot help. The
  // result was every ad on the page painting twice, the second time with its
  // header-bidding targeting cleared. Holding the batch until the product is on
  // screen makes it one auction. See ./ad-insert-batch.
  useAdInsertHold(!product?.id);

  const isOwnListing =
    profile && product?.user && product?.user?.id === profile?.id;

  return (
    <AllLayout header={true} search={true} nav={true} account={false} showAd={true}>
      <article className="page product-page same-color">
        {product?.id ? (
          <>
            <ProductDetail />
            <ChatModal isOwnListing={isOwnListing} id={product?.id} companion={product?.user} />
          </>
        ) : gone ? (
          <ListingGone listingId={String(id)} />
        ) : (
          <ProductSkeleton />
        )}
      </article>
    </AllLayout>
  );
};

export default ProductPage;
